import React, { useState, useEffect, useMemo } from 'react';
import { Form } from 'semantic-ui-react';
import moment from 'moment';
import './styles.css';
import { onSuccess, onError } from '../../components/shared/messages';
import GridSearchTemplate from '../GridSearchTemplate';
import { DatePicker } from '../../components/shared/DatePicker';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { Hotels } from "../../api/services/next";
import { useTextResources } from '../../language';
import {
  GetBankDetailsReport,
  UpdateFolioTransactionProperties,
} from '../../api/services/finance';
import { dateFormat, MenuActions } from '../../components/shared/constants';
import { useLoader } from '../../hooks/useLoader';
import { createExcelHeader } from '../../components/GridPanel/utility';
import DropDown from '../../components/shared/dropdown';

const HotelIDs = 'HotelIDs';

const BankDeposit = () => {
  const text = useTextResources();
  const [gridData, setGridData] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [searchState, setSearchState] = useState({});
  const [hotels, setHotels] = useState([]);
  const loader = useLoader();

  useEffect(() => {
    (async () => {
      try {
        const hotels = await Hotels.GetHotels();
        setHotels(hotels);
      } catch (error) {
        onError(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (window.optima && window.optima.commands) {
      window.optima.commands.unSubscribeAll(MenuActions.save);
      window.optima.commands.subscribe(MenuActions.save, save);
    }
  }, [gridData, updates]);

  const save = () => {
    (async () => {
      try {
        loader(true);
        if (updates && updates.length) {
          await UpdateFolioTransactionProperties({
            FolioTransactionsProperties: updates.map((x) => ({
              FolioTransactionId: x.Id,
              DepositDate: x.DepositDate,
            })),
          });
          setUpdates([]);
          onSuccess('DepositDateWasUpdatedSuccessfully');
        }
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    })();
  };

  const dateFormatter = ({ value }) => {
    return value && moment(value).format(dateFormat);
  };
  const amountFormatter = ({ value, row }) => {
    return `${value ? value.toFixed(2) : 0.0}${row ? row.CurrencySymbol : ''}`;
  };

  const isCalendarClick = (element) => {
    if (element && element.classList) {
      return (
        element.classList.contains('clndr-calendars-segment') ||
        isCalendarClick(element.parentNode)
      );
    } else {
      return false;
    }
  };

  const getExcelHeaderTitle = () => {
    let chosenHotels = [];
    hotels.forEach((item, index) => {
      if (searchState[`HotelIDs[${index}]`]) {
        chosenHotels.push(item.Id);
      }
    });
    const hotelsData = chosenHotels
      ? hotels.filter((x) => chosenHotels.includes(x.Id))
      : '';
    let hotelsNames = '';
    hotelsData.forEach((item, index) => {
      hotelsNames += index > 0 ? `, ${item.Name}` : item.Name;
    });
    return `Bank deposit report for hotels: ${hotelsNames}, ${moment(
      searchState.StartDate
    ).format(dateFormat)} - ${moment(searchState.EndDate).format(
      dateFormat
    )}`;
  };

  /*
    If no hotels were chosen, send all hotels. The reduce is being used for uniformity purposes-
    When choosing a hotel, searchPanel translates the payload to {HotelIDs[X] : value} for each hotel.
    For that reason, payload was adjusted int that manner instead of sending an array when nothing (and thus all) was chosen.
    */
  const hotelIdsSpecMemoized = useMemo(
    () =>
      searchState['HotelIDs[0]'] ||
      hotels?.reduce((spec, { Id }, idx) => {
        spec[`${HotelIDs}[${idx}]`] = Id;
        return spec;
      }, {}),
    [searchState, hotels]
  );

  const gridOptions = {
    search: {
      title: 'Bank Deposit',
      fields: [
        <Form.Field
          width='5'
          multiple
          clearable
          control={HotelsDropDown}
          className='bank-deposit-hotels'
          placeholder={text('All')}
          label='Hotel'
          key='HotelIDs'
        />,
        <DatePicker
          type='basic'
          label={text('StartDate')}
          name='StartDate'
          key='StartDate'
        />,
        <DatePicker
          type='basic'
          label={text('EndDate')}
          name='EndDate'
          key='EndDate'
        />,
        <DatePicker
          type='basic'
          label={text('DepositDate')}
          name='DepositDate'
          key='DepositDate'
        />,
        <Form.Field
          key='DepositedTransactions'
          control={DropDown}
          data={[
            {
              key: 0,
              text: 'All',
            },
            {
              text: 'Only Deposited',
              key: 1,
            },
            {
              text: 'Only Not deposited',
              key: 2,
            },
          ]}
          label='Deposited'
          valueField='key'
          textField='text'
          selectFirst
        />,
      ],
      api: GetBankDetailsReport,
      spec: { ...hotelIdsSpecMemoized },
    },
    grid: {
      columns: [
        {
          name: 'BusinessDate',
          title: 'Date',
          formatter: dateFormatter,
          editingEnabled: false,
        },
        {
          name: 'PaymentTypeName',
          title: 'Payment Type',
          editingEnabled: false,
        },
        {
          name: 'AmountBeforeTax',
          title: 'Amount',
          editingEnabled: false,
          formatter: amountFormatter,
        },
        { name: 'CurrencyName', title: 'Currency', editingEnabled: false },
        {
          name: 'ReservationNumber',
          title: 'Reservation Number',
          editingEnabled: false,
        },
        { name: 'HotelName', title: 'Hotel', editingEnabled: false },
        {
          name: 'DepositDate',
          title: 'Deposit Date',
          formatter: dateFormatter,
          editingEnabled: true,
          editor: (
            <DatePicker type='basic' name='DepositDate' key='DepositDate' />
          ),
          onEditorBlur: (e) => {
            return !isCalendarClick(e.relatedTarget);
          },
          customProps: ({ row, onBlur, value }) => {
            return {
              value: value && moment(value).toDate(),
              onChange: (value) => {
                setGridData((prev) => {
                  const item = gridData.find((x) => x.Id === row.Id);
                  item.DepositDate = moment(value).toISOString();
                  return [...prev];
                });
                setUpdates((prev) => {
                  const item = updates.find((x) => x.Id === row.Id);
                  if (item) {
                    item.DepositDate = moment(value).toISOString();
                    return [...prev];
                  } else {
                    return [...prev, row];
                  }
                });
                onBlur();
              },
            };
          },
        },
      ],
      editable: true,
      summary: true,
      export: true,
      cacheId: 'BankDeposit',
      customOptions: {
        group: {
          grouping: [
            { columnName: 'PaymentTypeName' },
            { columnName: 'CurrencyName' },
          ],
          defaultExpandedGroups: ['Cash', 'Check'],
        },
      },
      groupSummaryItems: [
        {
          columnName: 'AmountBeforeTax',
          type: 'sum',
          showInGroupFooter: true,
          alignByColumn: true,
        },
      ],
      columnExtensions: [
        { columnName: 'BusinessDate', editingEnabled: false },
        { columnName: 'PaymentTypeName', editingEnabled: false },
        { columnName: 'AmountBeforeTax', editingEnabled: false },
        { columnName: 'CurrencyName', editingEnabled: false },
        { columnName: 'ReservationNumber', editingEnabled: false },
        { columnName: 'HotelName', editingEnabled: false },
      ],
      excel: {
        customizeHeader: createExcelHeader(getExcelHeaderTitle()),
        grouping: [
          { columnName: 'PaymentTypeName' },
          { columnName: 'CurrencyName' },
        ],
        totalSummaryItems: [{ columnName: 'AmountBeforeTax', type: 'sum' }],
        groupSummaryItems: [
          {
            columnName: 'AmountBeforeTax',
            type: 'sum',
            showInGroupFooter: false,
            alignByColumn: true,
          },
        ],
      },
    },
  };
  return (
    <GridSearchTemplate
      options={gridOptions}
      onSearchComplete={(data) => setGridData(data)}
      data={gridData}
      onSearchChange={setSearchState}
    />
  );
};
export default BankDeposit;
